import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { DEFAULT_NOTIFICATION_PAYLOAD, MODAL, NOTIFICATION_VARIANTS, SORT_OPTIONS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "./helpers";
import { showNotification } from "../actions/notification";
import { requestCloseSlideOver } from "../actions/slide_over";
import { requestCloseModal, requestOpenModal } from "../actions/modal";
import { setProcessingState } from "../reducers/modalConfirmation";

export const defaultNeedsListTemplatesFilters = {
  page: 1,
  size: 10,
  sortBy: SORT_OPTIONS[1].id,
  searchTerm: "",
};

export const needsListTemplateService = createApi({
  reducerPath: "needsListTemplateApi",
  tagTypes: ["NeedsListTemplate"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getNeedsListTemplates: builder.query({
      query: params => `needs-list-templates?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: "NeedsListTemplate", id })),
              { type: "NeedsListTemplate", id: "LIST" },
            ]
          : [{ type: "NeedsListTemplate", id: "LIST" }],
    }),
    getNeedsListTemplate: builder.query({
      query: id => `needs-list-templates/${id}`,
      providesTags: (result, error, id) => [{ type: "NeedsListTemplate", id }],
    }),
    addNeedsListTemplate: builder.mutation({
      query: body => ({
        url: "needs-list-templates",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NeedsListTemplate", id: "LIST" }],
      async onQueryStarted({ closeSlide = true }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_TEMPLATE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.name} has been created!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          if (closeSlide) dispatch(requestCloseSlideOver());
          dispatch(
            requestOpenModal(MODAL.NEEDS_LIST_TEMPLATE_DOCUMENTS, {
              needsListTemplateId: data.needsListTemplateId,
              name: data.name,
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    addNeedsListTemplateFromBorrowerNeedsList: builder.mutation({
      query: ({ loanId, borrowerId, ...body }) => ({
        url: `needs-list-templates/loans/${loanId}/borrowers/${borrowerId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NeedsListTemplate", id: "LIST" }],
      async onQueryStarted({ closeSlide = true }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_TEMPLATE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: `${data.name} template has been created!`,
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          if (closeSlide) dispatch(requestCloseSlideOver());
          dispatch(
            requestOpenModal(MODAL.NEEDS_LIST_TEMPLATE_DOCUMENTS, {
              needsListTemplateId: data.needsListTemplateId,
              name: data.name,
            }),
          );
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    updateNeedsListTemplate: builder.mutation({
      query: ({ needsListTemplateId: id, ...patch }) => ({
        url: `needs-list-templates/${id}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "NeedsListTemplate", id }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_TEMPLATE_UPDATED, { ...data });
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Template has been updated!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseSlideOver());
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
    deleteNeedsListTemplate: builder.mutation({
      query(id) {
        return {
          url: `needs-list-templates/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "NeedsListTemplate", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_TEMPLATE_DELETED, { ...data });
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Template has been deleted!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseSlideOver());
          dispatch(requestCloseModal());
        } catch (err) {
          dispatch(setProcessingState(false));
          Bugsnag.notify(err);
        }
      },
    }),
    cloneNeedsListTemplate: builder.mutation({
      query({ needsListTemplateId: id, ...patch }) {
        return {
          url: `needs-list-templates/${id}/clone`,
          method: "POST",
          body: patch,
        };
      },
      invalidatesTags: [{ type: "NeedsListTemplate", id: "LIST" }],
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.NEEDS_LIST_TEMPLATE_CLONED, { ...data });
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Template has been cloned!",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(requestCloseSlideOver());
          dispatch(requestCloseModal());
        } catch (err) {
          dispatch(setProcessingState(false));
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  useGetNeedsListTemplatesQuery,
  useGetNeedsListTemplateQuery,
  useAddNeedsListTemplateMutation,
  useAddNeedsListTemplateFromBorrowerNeedsListMutation,
  useUpdateNeedsListTemplateMutation,
  useDeleteNeedsListTemplateMutation,
  useCloneNeedsListTemplateMutation,
} = needsListTemplateService;
